import React, { createContext, useContext, useEffect, useRef } from 'react';

import {
  REALTIME_BACKEND_ANYCABLE,
  REALTIME_BACKEND_PUSHER,
  RealtimeBackend,
} from 'ev-types';

import { useNullableCurrentPractice } from 'ev-hooks/commonData';
import { getRealtimeBackend } from 'ev-utils/practice';

import { disconnectAnycable } from './anycable/connection';

const RealtimeBackendContext = createContext<RealtimeBackend>(
  REALTIME_BACKEND_PUSHER,
);

/**
 * Read the active realtime backend from context.
 * All subscription hooks should use this instead of independently
 * reading the practice preference.
 */
export function useRealtimeBackend(): RealtimeBackend {
  return useContext(RealtimeBackendContext);
}

/**
 * Detect the realtime backend from the current practice and provide it to all
 * descendant hooks. When the backend changes (e.g. practice switch), disconnect
 * the previous client so there are no stale connections.
 *
 * Place inside AppProviders (after CommonDataProvider so practice is available).
 */
export const RealtimeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const practice = useNullableCurrentPractice();
  const backend = getRealtimeBackend(practice);
  const prevBackendRef = useRef<RealtimeBackend | null>(null);

  useEffect(() => {
    const prev = prevBackendRef.current;
    prevBackendRef.current = backend;

    if (prev !== null && prev !== backend) {
      if (prev === REALTIME_BACKEND_PUSHER) {
        // Dynamic import avoids pulling in pusher.ts side effects at module load
        // time (Pusher.logToConsole reads ENVIRONMENT_NAME which breaks Jest setup).
        // Safe from race conditions: by the time we switch *away* from Pusher,
        // the Pusher hooks have already loaded the module so it resolves from
        // the module cache. If it somehow wasn't loaded, disconnectPusher()
        // sees gPusherWithAuth === null and no-ops.
        void import('../pusher/pusher')
          .then(m => m.disconnectPusher())
          .catch(() => {
            // Chunk load failure or similar — nothing to disconnect.
          });
      }
      if (prev === REALTIME_BACKEND_ANYCABLE) {
        disconnectAnycable();
      }
    }
  }, [backend]);

  return (
    <RealtimeBackendContext.Provider value={backend}>
      {children}
    </RealtimeBackendContext.Provider>
  );
};

/**
 * Disconnect both Pusher and Anycable clients (e.g. on logout).
 */
export async function disconnectAllRealtime(): Promise<void> {
  try {
    const { disconnectPusher } = await import('../pusher/pusher');
    disconnectPusher();
  } catch {
    // Chunk load failure — nothing to disconnect.
  }
  disconnectAnycable();
}
